import { createBrowserRouter, type RouteObject } from "react-router";

import { RequireAuth } from "@/auth/RequireAuth";
import { GuideIndexPage } from "@/guide/GuideIndexPage";
import type { GuideGroupKey } from "@/guide/topics";
import { AppShell } from "@/layout/AppShell";
import { AccountPage } from "@/pages/AccountPage";
import { LoginPage } from "@/pages/LoginPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { OverviewPage } from "@/pages/OverviewPage";
import { ServicesPage } from "@/pages/ServicesPage";
import { buildServiceRoutes } from "@/services/buildServiceRoutes";
import { SERVICE_MODULES } from "@/services/registry";

/**
 * 路由總表。
 *
 * 對應導覽的四個分節（見 `layout/navSections.ts`）：
 *
 *   /               總覽 —— 跨服務的儀表，標誌也指向這裡
 *   /services       服務 —— 服務清單，第二層選單列出各服務與其子頁
 *   /notifications  通知 —— 施工中
 *   /account        個人中心
 *   /account/guide  使用指南 —— 入口在右上角的帳號選單，所以歸在個人中心底下；
 *                   底下分 general／services／admin 三組（見 `guide/topics.ts`）
 *
 * 服務自己的路由不在這裡手寫 —— 由 `services/registry.ts` 的 SERVICE_MODULES
 * 產生，而那份又是從每個服務自己的 `pages.ts` 來的。新增服務或子頁都不用
 * 動這個檔案。
 *
 * 服務掛在根底下（`/master/items`）而不是 `/services/master/items`：網址短、好記，
 * 服務代碼在後端保證唯一所以不會撞。代價是 `navSections.activeSection()`
 * 必須拿使用者實際有的服務代碼來比對，不能只看前綴。
 */
/**
 * 使用指南的文章頁 lazy 載入：Markdown 渲染器與所有篇章的文字只有看教學時
 * 才需要，不該讓每個使用者一進系統就下載。
 */
function guideArticle(group: GuideGroupKey): NonNullable<RouteObject["lazy"]> {
  return async () => {
    const { GuideArticlePage } = await import("@/guide/GuideArticlePage");
    return {
      Component: function GuideArticleRoute() {
        return <GuideArticlePage group={group} />;
      },
    };
  };
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <OverviewPage /> },
          { path: "services", element: <ServicesPage /> },
          { path: "notifications", element: <NotificationsPage /> },
          { path: "account", element: <AccountPage /> },
          { path: "account/guide", element: <GuideIndexPage /> },
          { path: "account/guide/general/:topicKey", lazy: guideArticle("general") },
          { path: "account/guide/services/:topicKey", lazy: guideArticle("services") },
          { path: "account/guide/admin/:topicKey", lazy: guideArticle("admin") },
          ...SERVICE_MODULES.map(buildServiceRoutes),
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
